import React, { ChangeEvent, ReactElement, useCallback, useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { IAppState } from '../../../core/store/store'
import { withPermissionsHOC } from '../../../core/hoc/permissionsHoc'
import {
  Button,
  ButtonType,
  Checkbox,
  Checkboxes,
  Form,
  FormFieldSize,
  FormGroup,
  Input,
  InputType,
  Select,
  Stepper,
  SubmitButton
} from '@digicert/dcone-common-ui'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faClock, faPencil } from '@fortawesome/pro-light-svg-icons'
import styles from './CreateUser.module.scss'
import { Translate } from 'react-localize-redux'
import { mapOptionsCommonUi, mapUserPermission, translate } from '../../../shared/helpers/utils'
import { ICreateUserProps } from './CreateUser.types'
import { getPermissions } from '../../../core/selectors/Profile.selectors'
import NoPermissionsPage from '../../../shared/components/no-permissions-page'
import { fetchPermissions } from '../../../core/actions/Profile.actions'
import { FormMode } from '../../../shared/interfaces'
import UserForm from '../user-form'
import { EMAIL_REGEXP, RouterConfig, USER_NAME_REGEXP } from '../../../core/utils/constants'
import { IAccessScope } from '../../../reducers/Reducers.interfaces'
import { createUser } from '../../../core/actions/Users.actions'
import history from 'core/history/history'
import { IUserFormValues } from '../user-form/UserForm.types'
import { destroyForm } from '../../../core/actions/Common'
import { UserFormAdapter } from '../user-form/UserForm.constants'
import { omit } from 'lodash-es'
import { AccountFeatures } from 'core/constants'
import { checkIfUsernameExists } from 'public-pages/self-service-user/SelfServiceUser.actions'

const CreateUser = (
  {
    createUser,
    destroyForm,
    fetchPermissions,
    checkIfUsernameExists,
    localeList,
    location,
    profileAccessScope,
    permissions,
    profile,
  }: ICreateUserProps): ReactElement => {
  const selectText = translate('common.form.select') as string  
  const [currentPage, setCurrentPage] = useState(0)
  const [systemUserChecked, setSystemUserChecked] = useState(false);
  const [account, setAccount] = useState(selectText)
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [userName, setUserName] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [language, setLanguage] = useState(selectText)
  const [featureIds, setFeatureIds] = useState<string[]>([])
  const [userNameExists, setUserNameExists] = useState(false)
  const [keyLockerEnabled, setKeyLockerEnabled] = useState(false)

  const getAccounts = () => {
    const options = profile?.accounts.filter(account => account.active === true).map((account) => {
      return {
        label: account.name,
        value: account.id,
        locale: account.locale,
        featureIds: account.enabled_feature_ids
      }
    })
    options?.sort((a, b) => (a.label > b.label ? 1 : -1))
    return options
  }

  const onAccountChange = (value) => {
    setAccount(value)
    if (profile && value) {
      setKeyLockerEnabled(profile.accounts.filter(acc => acc.id === value)[0].enabled_feature_ids.includes(AccountFeatures.STM_KEYLOCKER_ACCOUNT))
  }
    if(!systemUserChecked) {
      fetchPermissions(IAccessScope.account.toUpperCase(), value)
      const selectedAccount = getAccounts()?.find(item => item.value === value)
      if(selectedAccount){
        setLanguage(selectedAccount.locale)
        setFeatureIds(selectedAccount.featureIds)
      }
    }
    else {
      setLanguage(selectText)
    }
  }

  const validateAccountSelect = (): any => {
    return !account ? translate('common.form.validation.required') : ''
  }

  const onFirstNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFirstName(event.target.value)
  }

  const onLastNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setLastName(event.target.value)
  }

  const onEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value)
  }

  const onUserNameFocus = () => {
    if(userName === '') {
      setUserName(email)
      checkIfUsernameExists(email).then((response:boolean) => {
        setUserNameExists(response)
      })
    }
    else {
      setUserName(userName)
    }
  }

  const validateUserName = (): any => {
    if (userNameExists) {
      return translate('notifications.exceptions.USERNAME_EXISTS');
    }
  }

  const onUserNameChange = useCallback(
    (event) => {
      setUserName(event.target.value)
      checkIfUsernameExists(event.target.value).then((response:boolean) => {
        setUserNameExists(response)
      })
    },
    [checkIfUsernameExists]
  )

  const onPhoneChange = (value) => {
    value ? setPhoneNumber(value) : setPhoneNumber('')
  }

  const onLangChange = (lang: any): void => {
    setLanguage(lang)
  }

  const validateLangSelect = (): any => !language ? translate('common.form.validation.required') : ''

  const cancelUserCreation = () => {
    history.goBack()
  }

  const proceedToPermissions = (e) => {
    e.preventDefault()
    setCurrentPage(currentPage + 1)
  }

  const formatAccountArrayForPayload = () => {
    const selectedAccount = getAccounts()?.find(item => item.value === account)
    let formatAccountArray : any[] = [];

    if(selectedAccount) {
      formatAccountArray = [selectedAccount].map(item => {
        return {
          id: item.value,
          name: item.label };
      });
    }

    return formatAccountArray
  }

  const submitNewUser = (formValues: IUserFormValues) => {
    const formData = {
      ...omit(formValues, ['user', 'allPermissions']),
      first_name: firstName,
      last_name: lastName,
      email: email,
      user_name: userName,
      permissions: mapUserPermission(formValues.permissions),
      accounts: !systemUserChecked ? formatAccountArrayForPayload() : [],
      access_scope: systemUserChecked,
      locale: language,
      language: localeList.locales.find(item => item.id === language),
      phone: phoneNumber
    }

    // creates user, must destroy form then navigate to admin table
    // not destroying form will retain form data when navigating via history
    createUser(formData).then(() => {
      destroyForm(UserFormAdapter.name).then(() => {
        history.push(RouterConfig.administrators.table)
      })
    })
  }

  const onStepChange = (currentPage) => {
    setCurrentPage(currentPage)
  }

  const onSystemUserCheckBoxChange = useCallback((event) => {
    setSystemUserChecked(event.checked)
    if(event.checked) {
      setLanguage(language)
      fetchPermissions(IAccessScope.system.toUpperCase()).then()
    }
  }, [fetchPermissions, language]);

  useEffect(() => {
    destroyForm(UserFormAdapter.name).then()
  }, [UserFormAdapter.name])

  if (permissions.canManageAccountUser
    ? false
    : !permissions.canManageSystemUser) {
    return <NoPermissionsPage />
  }

  return (
    <>
      <div className={styles.stepWrapper}>
        <Stepper
          headline={<h1>{translate('manageUser.administrators.form.createAdmin')}</h1>}
          currentStep={currentPage}
          stepContentMaxWidth={400}
          onStepChange={onStepChange}
          stepData={[
            {
              label: translate('manageUser.administrators.form.nameAccessRights'),
              icon: <FontAwesomeIcon size='lg' icon={faPencil} />
            },
            {
              label: translate('manageUser.administrators.form.adminPermissionsStepLabel'),
              icon: <FontAwesomeIcon size='lg' icon={faClock} />
            }
          ]}
        />
      </div>

      {currentPage === 0 && (
        <section className={styles.createUser}>
          <h2><Translate id='manageUser.administrators.form.generalInfo'/></h2>
          <Form center={false} onSubmit={(e) => {proceedToPermissions(e)}}>
            {profileAccessScope === IAccessScope.system && permissions.canManageSystemUser &&
            <FormGroup>
              <Checkboxes>
                <Checkbox
                  label={translate('common.form.systemUser')}
                  value={translate('common.form.systemUser') as string}
                  onChange={onSystemUserCheckBoxChange}
                  checked={systemUserChecked}
                />
              </Checkboxes>
            </FormGroup>
            }

            { ((profileAccessScope === IAccessScope.system && !systemUserChecked) || (profileAccessScope === IAccessScope.account && !systemUserChecked)) &&
            <FormGroup>
              <Select
                getPopupContainer={(triggerNode) => triggerNode.parentElement}
                size={FormFieldSize.L}
                allowClear
                defaultValue={account}
                value={account}
                label={translate('common.form.account')}
                placeholder={translate('common.form.select')}
                onChange={onAccountChange}
                options={getAccounts()}
                errors={{valueMissing: translate('common.form.validation.required')}}
                validate={validateAccountSelect}
                optionFilterProp="label"
                showSearch
              />
            </FormGroup>
            }

            <div className={styles.nameGroup}>
              <Input
                label={<Translate id='common.form.firstName'/>}
                required
                value={firstName}
                errors={{valueMissing: translate('common.form.validation.required')}}
                onChange={onFirstNameChange}
              />
              <Input
                label={<Translate id='common.form.lastName'/>}
                required
                value={lastName}
                errors={{valueMissing: translate('common.form.validation.required')}}
                onChange={onLastNameChange}
              />
            </div>

            <FormGroup>
              <Input
                size={FormFieldSize.L}
                label={<Translate id='common.form.email'/>}
                required
                value={email}
                onChange={onEmailChange}
                pattern={EMAIL_REGEXP}
                errors={{
                  typeMismatch: translate('notifications.errors.invalidEmailError'),
                  valueMissing: translate('common.form.validation.required'),
                  patternMismatch: translate('notifications.errors.invalidEmailError')
                }}
              />
            </FormGroup>

            <FormGroup>
              <Input
                size={FormFieldSize.L}
                label={<Translate id='common.form.username'/>}
                required
                value={userName}
                pattern={USER_NAME_REGEXP}
                errors={{
                  valueMissing: translate('common.form.validation.required'),
                  patternMismatch: translate('common.form.validation.invalidUserName')
                }}
                onChange={onUserNameChange}
                onFocus={onUserNameFocus}
                validate={validateUserName}
              />
            </FormGroup>

            <FormGroup>
              <Input
                size={FormFieldSize.L}
                label={translate('common.form.phoneOptional')}
                type={InputType.TELEPHONE}
                validateChanges
                value={phoneNumber}
                errors={{valueMissing: translate('common.form.validation.required')}}
                onChange={onPhoneChange}
              />
            </FormGroup>

            <FormGroup>
              <Select
                getPopupContainer={(triggerNode) => triggerNode.parentElement}
                size={FormFieldSize.L}
                allowClear
                defaultValue={language}
                value={language}
                label={translate('profile.language')}
                onChange={onLangChange}
                options={mapOptionsCommonUi(localeList.locales)}
                errors={{valueMissing: translate('common.form.validation.required')}}
                validate={validateLangSelect}
                optionFilterProp="label"
                showSearch
              />
            </FormGroup>

            <div className={styles.formActions}>
              <Button buttonType={ButtonType.SECONDARY} onClick={cancelUserCreation}>
                <Translate id='common.form.buttons.cancelBtn'/>
              </Button>

              <SubmitButton disabled={!systemUserChecked && !account || !systemUserChecked && account === selectText || language === selectText}>
                <Translate id='common.form.buttons.nextBtn'/>
              </SubmitButton>
            </div>
          </Form>
        </section>
      )}

      {currentPage === 1 && (
        <section className={styles.createUserPermissions}>
          <UserForm
            goBack={()=>setCurrentPage(0)}
            mode={FormMode.create}
            location={location}
            onSubmit={submitNewUser}
            samlLogin={false}
            loading={false}
            step={1}
            canManagePermission={permissions.canManagePermission}
            permissions={permissions}
            currentAccessScope={systemUserChecked}
            features={featureIds}
            keylockerEnabled={keyLockerEnabled}
          />
        </section>
      )}
    </>
  )
}

export default connect((state: IAppState) => ({
    permissions: getPermissions(state),
    profile: state.profile.data,
    localeList: state.locales,
    profileAccessScope: state.profile.data?.access_scope
  }),
  { createUser, fetchPermissions, destroyForm, checkIfUsernameExists }
)(withPermissionsHOC(CreateUser, ['canViewUser']))